<script setup lang="ts">
import { ref, watch } from 'vue'
import { Icon } from '@iconify/vue'

interface ProjectInfoRow {
  key: string
  value: string
}

interface ProjectInfoFormLike {
  initialValues?: {
    project_information?: ProjectInfoRow[]
  }
  setFieldValue?: (field: string, value: ProjectInfoRow[]) => void
  fields?: {
    project_information?: {
      states?: {
        error?: {
          message?: string
        }
      }
    }
  }
}

interface Props {
  form: ProjectInfoFormLike
  editable: boolean
}

const props = defineProps<Props>()

const rows = ref<ProjectInfoRow[]>([])

const cloneRows = (items: ProjectInfoRow[]) => items.map((item) => ({ key: item.key || '', value: item.value || '' }))

const syncForm = () => {
  props.form?.setFieldValue('project_information', cloneRows(rows.value))
}

const addRow = () => {
  rows.value.push({ key: '', value: '' })
  syncForm()
}

const removeRow = (index: number) => {
  if (rows.value.length <= 1) return
  rows.value.splice(index, 1)
  syncForm()
}

watch(
  () => props.form?.initialValues?.project_information,
  (projectInformation?: ProjectInfoRow[]) => {
    const incoming = Array.isArray(projectInformation) ? cloneRows(projectInformation) : []
    rows.value = incoming.length > 0 ? incoming : [{ key: '', value: '' }, { key: '', value: '' }]
  },
  { immediate: true },
)
</script>

<template>
  <div class="estate-project-section-body">
    <InputText type="text" name="project_information" hidden />

    <div class="estate-project-section-header">
      <label class="estate-project-section-title">Thông tin dự án <span>*</span></label>
      <button type="button" class="estate-project-section-link" :disabled="!editable" @click="addRow">
        + Thêm giá trường thông tin
      </button>
    </div>

    <div class="estate-project-info-rows">
      <div class="estate-project-info-row" v-for="(row, index) in rows" :key="`project-info-${index}`">
        <input
          v-model="row.key"
          class="estate-project-info-input"
          placeholder="Tiêu đề"
          :readonly="!editable"
          @input="syncForm"
        />
        <input
          v-model="row.value"
          class="estate-project-info-input"
          placeholder="Giá trị"
          :readonly="!editable"
          @input="syncForm"
        />
        <button
          type="button"
          class="estate-project-info-remove"
          :disabled="!editable"
          :aria-label="`Xóa dòng thông tin ${index + 1}`"
          @click="removeRow(index)"
        >
          <Icon icon="lsicon:minus-filled" width="22" height="22" />
        </button>
      </div>
    </div>

    <span class="estate-project-error">{{ props.form?.fields?.project_information?.states?.error?.message }}</span>
  </div>
</template>

<style scoped>
.estate-project-section-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.estate-project-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.estate-project-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #000;
}

.estate-project-section-title span {
  color: #ff0000;
}

.estate-project-section-link {
  font-size: 16px;
  font-weight: 700;
  color: #0035ba;
  background: transparent;
  border: 0;
  padding: 0;
}

.estate-project-section-link:disabled {
  opacity: 0.5;
}

.estate-project-info-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
}

.estate-project-info-row {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 22px;
  align-items: center;
  gap: 6px;
  padding: 7px 0;
}

.estate-project-info-input {
  background: #f7f7f7;
  border: 1px solid #f7f7f7;
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 12px;
  color: #000;
}

.estate-project-info-input::placeholder {
  color: #777;
}

.estate-project-info-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #e11d48;
  padding: 0;
}

.estate-project-info-remove:disabled {
  opacity: 0.4;
}

.estate-project-error {
  display: block;
  font-size: 12px;
  font-style: italic;
  color: #dc2626;
}
</style>
